// Type-ahead editor for fieldType "contact" (PEOPLE-RECORD-1, D-202.1).
//
// Modelled on InstitutionInput, which is modelled on AddressInput: one visible input, our own
// brand-styled dropdown, a 250 ms debounce and full keyboard navigation. Everything about the
// matching, the keyboard model and the deferred-blur save is deliberately IDENTICAL — a planner
// should not have to learn two type-aheads.
//
// 🔴 FREE TEXT ALWAYS WINS (the s201 ruling, restated because it is the whole design). No
// suggestion is ever forced, Enter never auto-selects the top row, and a name nobody has typed
// before is a COMPLETE, CORRECT value.
//
// 🔴 THE ONE BEHAVIOUR THE INSTITUTION PILOT DOES NOT HAVE, and the reason this build exists:
// committing an unmatched name CREATES A CONTACT ROW and stores its id as the `ref`. Without
// that, a typed name is just a string again, and the second surface that names the same person
// starts another copy — which is precisely the defect D-202.1 was ruled to end. An institution
// the registry has never heard of stays a bare `{ display }` forever, because we do not own that
// list; a person the plan has never heard of becomes a record, because the plan owns its people.
//
// Three differences from InstitutionInput, and only these:
//   1. The suggestion source is per-client (`/api/clients/:id/contacts`), not a global registry.
//   2. Matching is over composed names, with no alias channel — a person has no aliases.
//   3. Commit mints a row (above). Everything else is the pilot's, unchanged.
//
// Values (de)serialize through @shared/contact so the server consumers share the exact same
// shape, and a legacy plain string typed before the fieldType flip renders unchanged (G8).

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { apiRequest } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { formatPhone } from "@shared/phone";
import { composeContactName, formatContactSummary, parseContact, splitContactName, toContactValue } from "@shared/contact";

const MIN_QUERY_LEN = 1;
const MAX_SUGGESTIONS = 8;
const DEBOUNCE_MS = 250;

export type ContactOption = {
  id: string;
  kind: "person" | "organisation";
  firstName: string | null;
  surname: string;
  relationship?: string | null;
  // CONTACT-CARD-1 (s205): served by the same picker GET, so the card needs no second fetch.
  phone?: string | null;
  email?: string | null;
};

/**
 * Case-insensitive prefix or word-start match on the contact's composed name.
 *
 * Word-start rather than substring, for the same reason the institution matcher gives:
 * "and" should offer "Anderson" and "Anna Anderson", but should not offer every contact with
 * "and" buried mid-word. There is no alias channel — a person does not have aliases — so this
 * matcher is the institution one minus that branch, never plus anything.
 */
export function matchContacts(
  query: string,
  options: readonly ContactOption[],
  limit = MAX_SUGGESTIONS,
): ContactOption[] {
  const q = query.trim().toLowerCase();
  if (q.length < MIN_QUERY_LEN) return [];
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const startsWord = (haystack: string): boolean => {
    const h = haystack.toLowerCase();
    if (h.startsWith(q)) return true;
    return new RegExp(`[\\s\\-/&.,(]${escaped}`).test(h);
  };
  return options
    .filter((o) => startsWord(composeContactName(o)))
    .sort((a, b) => {
      const an = composeContactName(a);
      const bn = composeContactName(b);
      const aPre = an.toLowerCase().startsWith(q) ? 0 : 1;
      const bPre = bn.toLowerCase().startsWith(q) ? 0 : 1;
      return aPre !== bPre ? aPre - bPre : an.localeCompare(bn);
    })
    .slice(0, limit);
}

export function ContactInput({ value, onChange, onBlur, readOnly, fieldId, clientId, placeholder, inCard }: {
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  readOnly?: boolean;
  fieldId: string;
  clientId?: string;
  placeholder?: string;
  /** Mounted inside the interview's ContactCard, which already shows Mobile, Email and the address —
   *  so this input offers no second, older editor of its own (s222 gate fixes, part C). */
  inCard?: boolean;
}) {
  // What the planner sees and edits: the display half of the stored value, or the raw string
  // when the value is a legacy plain string (G8 — it is shown, never discarded).
  const display = useMemo(() => formatContactSummary(value), [value]);

  const queryClient = useQueryClient();

  // One fetch per client per session. Unlike the institution registry this list CHANGES while
  // the planner works — every mint adds to it — so the cache is invalidated on create rather
  // than held forever. A failed fetch degrades to a plain input with no dropdown and no error
  // surfaced: the planner keeps typing and the value still saves, it just does not resolve.
  const { data: options } = useQuery<ContactOption[]>({
    queryKey: [`/api/clients/${clientId}/contacts`],
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    enabled: !readOnly && !!clientId,
  });

  const [suggestions, setSuggestions] = useState<ContactOption[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;
  // The name last committed to the record. A per-name latch: blur can fire more than once for one
  // edit, and a second POST would create a second row for the same human — the exact duplication
  // this record exists to end. It is the FIRST of two locks and it is not sufficient on its own,
  // because two blurs inside one tick both read the pre-write value.
  const mintedForRef = useRef<string | null>(null);

  // The SECOND lock, and the real interlock (D-169.10, § Canonical patterns). Minting a contact is
  // textbook Class-A — a second fire is a second created row — so it routes through
  // useSingleFlight, whose lock flips synchronously before React is involved. `disabled` would be
  // no use here at all: this fires from a blur handler, not a button.
  const mintFlight = useSingleFlight();

  const mintMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", `/api/clients/${clientId}/contacts`, { display: name });
      return (await res.json()) as ContactOption;
    },
  });

  // ── CONTACT-CARD-1 (s205) — charter §4B convention 5: "a CONTACT CARD under the row —
  // Name · Mobile · Email … one panel, Done. Closed, the row shows the person's name."
  //
  // 🔴 The card ARRIVES CLOSED and opens only when the planner asks. A card that auto-opened would
  // turn a clean list of rows into a wall — the same thing Reade cut from the chapter-one mockups.
  //
  // 🔴 It edits a CONTACT ROW, so it needs one to edit: it appears only when the stored value carries
  // a `ref`. A legacy plain string (G8) has no row behind it, so there is nothing to PATCH and no card
  // is offered — the name still shows and still saves, exactly as before.
  const contactRef = useMemo(() => parseContact(value)?.ref ?? null, [value]);
  const linked = useMemo(
    () => (contactRef ? options?.find((o) => o.id === contactRef) ?? null : null),
    [contactRef, options],
  );

  const [cardOpen, setCardOpen] = useState(false);
  const [draft, setDraft] = useState<{ firstName: string; surname: string; phone: string; email: string; kind: "person" | "organisation" }>(
    { firstName: "", surname: "", phone: "", email: "", kind: "person" },
  );

  // Seed the draft from the record each time the card opens, so a cancelled edit leaves nothing
  // behind and a reopen shows what is actually stored.
  const openCard = () => {
    setDraft({
      firstName: linked?.firstName ?? "",
      surname: linked?.surname ?? "",
      phone: linked?.phone ?? "",
      email: linked?.email ?? "",
      kind: linked?.kind ?? "person",
    });
    setCardOpen(true);
  };

  // 🔴 s205 (RULED) — Cancel discards the card's unsaved edits and closes it.
  //
  // The shipped card had Done only, so a planner who typed a wrong number could either save it or
  // guess that navigating away discarded it. Charter §4B convention 5 and every Planner Magic mockup
  // show Done ALONGSIDE Cancel; the omission was drift, not a decision.
  //
  // 🔴 It does NOT un-create the contact. The record was minted when the NAME was committed, before
  // this card ever opened — Cancel undoes what the card changed and nothing else. "Remove this
  // person" is what deletes, and the wording keeps those two apart.
  const cancelCard = () => setCardOpen(false); // the draft is re-seeded from the record on next open

  // 🔴 s205 — the correction path, and the ONLY place the person/organisation question can honestly
  // be answered. The mint no longer guesses: it stores the whole typed string in `surname`. Here a
  // human either leaves it whole (an organisation) or splits it (a person), and `splitContactName`
  // — unchanged, still tested — becomes the one-click SUGGESTION rather than a silent assumption.
  const splitSuggestion = () => {
    const whole = [draft.firstName, draft.surname].filter(Boolean).join(" ").trim();
    const { firstName, surname } = splitContactName(whole);
    setDraft((d) => ({ ...d, firstName, surname }));
  };

  const saveCard = useMutation({
    mutationFn: async () => {
      if (!contactRef) return;
      await apiRequest("PATCH", `/api/clients/${clientId}/contacts/${contactRef}`, {
        // `surname` is NOT NULL, so never send it empty — if the planner cleared it, the whole name
        // is whatever remains, which the route would reject as blank.
        ...(draft.surname.trim() ? { surname: draft.surname.trim() } : {}),
        firstName: draft.firstName.trim(),
        phone: draft.phone.trim(),
        email: draft.email.trim(),
        kind: draft.kind,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/contacts`] });
      setCardOpen(false);
    },
  });

  // The correction path this entry was registered for: a mistyped name minted a row that could not
  // be removed through the product. Soft delete — the name keeps rendering wherever it was already
  // used, and stops being offered in the picker.
  const removeContact = useMutation({
    mutationFn: async () => {
      if (!contactRef) return;
      await apiRequest("DELETE", `/api/clients/${clientId}/contacts/${contactRef}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/contacts`] });
      setCardOpen(false);
    },
  });

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  const handleChange = (typed: string) => {
    // Typing REPLACES any previous match: the ref described the old name, so carrying it
    // forward would leave a value whose pointer and text disagree. It is re-earned by picking a
    // suggestion, or by the mint on commit.
    onChange(toContactValue(typed));
    mintedForRef.current = null; // a new string is a new commit
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const list = matchContacts(typed, options ?? []);
      setSuggestions(list);
      setActiveIndex(-1);
      setOpen(list.length > 0);
    }, DEBOUNCE_MS);
  };

  const selectSuggestion = (option: ContactOption) => {
    setOpen(false);
    setSuggestions([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const name = composeContactName(option);
    mintedForRef.current = name; // picked an existing row — never mint for this name
    onChange(toContactValue(name, option.id));
    // Defer the save one tick, for the same reason AddressInput and InstitutionInput do:
    // onChange writes into the parent's local-edit state asynchronously and the parent's blur
    // handler READS that state to persist it. Calling blur synchronously reads the pre-change
    // value and nothing is saved.
    setTimeout(() => onBlurRef.current(), 0);
  };

  /**
   * Commit an unmatched name: mint the contact, then store `{ display, ref }`.
   *
   * 🔴 The save is NEVER gated on the mint. `onBlur()` fires first and unconditionally, so a
   * failed or slow POST leaves the planner with exactly what they typed — a bare `{ display }`,
   * which is a complete and correct value. The pointer is an improvement layered on top, never
   * a precondition for keeping the answer. Free text always wins includes free text winning
   * when the network does not.
   */
  const mintIfUnmatched = (typed: string) => {
    const name = typed.trim();
    if (!name || !clientId) return;
    if (mintedForRef.current === name) return;
    // An exact existing contact is a match even if the planner never opened the dropdown —
    // typing a name in full and tabbing away must not create a second row for that person.
    const existing = (options ?? []).find(
      (o) => composeContactName(o).toLowerCase() === name.toLowerCase(),
    );
    if (existing) {
      mintedForRef.current = name;
      onChange(toContactValue(composeContactName(existing), existing.id));
      setTimeout(() => onBlurRef.current(), 0);
      return;
    }
    mintedForRef.current = name;
    void mintFlight
      .run(() => mintMutation.mutateAsync(name))
      .then((created) => {
        // `run` resolves undefined when it rejected the call as a duplicate — that is the lock
        // doing its job, not a failure, so there is nothing to do and nothing to report.
        if (!created?.id) return;
        // Fire the refetch WITHOUT awaiting it, and OUTSIDE the lock: the window held is the
        // mutation window, never the refetch window (the hook's own rule).
        void queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/contacts`] });
        onChange(toContactValue(name, created.id));
        setTimeout(() => onBlurRef.current(), 0);
      })
      .catch(() => {
        // Deliberately silent, and the latch is released so a later blur can retry. The typed
        // value is already saved and already correct; telling the planner that a background
        // record-keeping step failed asks them to act on something they cannot fix and did not
        // ask for.
        mintedForRef.current = null;
      });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      // Enter with nothing highlighted commits what the planner TYPED. It never auto-selects
      // the top suggestion — that is how free typing gets silently overwritten by a near-match.
      if (activeIndex >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleBlur = () => {
    onBlur(); // field autosave — the typed value stands, matched or not, minted or not
    mintIfUnmatched(display);
    blurTimerRef.current = setTimeout(() => setOpen(false), 150);
  };

  if (readOnly) {
    return display
      ? <span className="text-sm">{display}</span>
      : <span className="text-sm text-muted-foreground">—</span>;
  }

  return (
    <div className="relative" data-testid={`input-contact-${fieldId}`}>
      <Input
        value={display}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder ?? "Start typing a name…"}
        className="text-sm"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {open && suggestions.length > 0 && (
        <ul className="address-suggestions" role="listbox" data-testid={`contact-suggestions-${fieldId}`}>
          {suggestions.map((o, i) => (
            <li
              key={o.id}
              role="option"
              aria-selected={i === activeIndex}
              className={`address-suggestion-item${i === activeIndex ? " is-active" : ""}`}
              // mousedown (not click) + preventDefault keeps focus on the input so the
              // blur-driven close doesn't fire before the selection lands.
              onMouseDown={(e) => {
                e.preventDefault();
                selectSuggestion(o);
              }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              {composeContactName(o)}
              {o.relationship ? <span className="text-muted-foreground"> · {o.relationship}</span> : null}
            </li>
          ))}
        </ul>
      )}

      {/* The card's affordance. Only where a contact row exists to edit (see the note above), and
          never inside the interview's ContactCard, which already holds these answers (part C). */}
      {contactRef && !cardOpen && !inCard && (
        <button
          type="button"
          onClick={openCard}
          className="mt-1 text-[12px] font-medium text-[#4F6B51] hover:underline"
          data-testid={`contact-card-open-${fieldId}`}
        >
          Phone &amp; email
        </button>
      )}

      {contactRef && cardOpen && (
        <div
          className="mt-2 rounded-md border border-warm-stone bg-white p-3"
          data-testid={`contact-card-${fieldId}`}
        >
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-[12px] font-medium text-foreground" data-testid={`contact-card-name-${fieldId}`}>
              {[draft.firstName, draft.surname].filter(Boolean).join(" ").trim() || display}
            </p>
            {/* Offered, never applied automatically — the mint no longer guesses (s205). And NOT
                offered at all once the planner has said this is a company: splitting a company name
                into a given name and a family name is the mistake the mint fix removed. */}
            {draft.kind !== "organisation" && (
              <button
                type="button"
                onClick={splitSuggestion}
                className="text-[11px] text-muted-foreground hover:text-foreground hover:underline"
                data-testid={`contact-card-split-${fieldId}`}
              >
                Split into first + last
              </button>
            )}
          </div>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-[11px] text-muted-foreground">First name(s)</span>
              <input
                className="rounded border border-warm-stone bg-white px-2 py-1 text-sm"
                value={draft.firstName}
                onChange={(e) => setDraft((d) => ({ ...d, firstName: e.target.value }))}
                placeholder="Leave blank for an organisation"
                data-testid={`contact-card-firstname-${fieldId}`}
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[11px] text-muted-foreground">Surname or organisation</span>
              <input
                className="rounded border border-warm-stone bg-white px-2 py-1 text-sm"
                value={draft.surname}
                onChange={(e) => setDraft((d) => ({ ...d, surname: e.target.value }))}
                placeholder="Family name, or the whole company name"
                data-testid={`contact-card-surname-${fieldId}`}
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[11px] text-muted-foreground">Mobile</span>
              {/* 🔴 s205 — the product's ONE phone convention, which this box was ignoring. The 25
                  `phone`-typed template fields render `type="tel"` + `inputMode="tel"` and format on
                  blur; `formatPhone` lived unexported in a page file, so the one place a phone will
                  now be captured for a person was the one place that did not follow it. Same helper,
                  same blur moment, same behaviour — including leaving an international number and
                  any non-standard length untouched. */}
              <input
                type="tel"
                inputMode="tel"
                // s228 (item 69): no browser autofill on a phone box, as the contact card's (PersonPicker).
                autoComplete="off"
                className="rounded border border-warm-stone bg-white px-2 py-1 text-sm"
                value={draft.phone}
                onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                onBlur={() => setDraft((d) => ({ ...d, phone: formatPhone(d.phone) }))}
                placeholder="(555) 000-0000"
                data-testid={`contact-card-phone-${fieldId}`}
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[11px] text-muted-foreground">Email</span>
              <input
                className="rounded border border-warm-stone bg-white px-2 py-1 text-sm"
                value={draft.email}
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                placeholder="Email address"
                data-testid={`contact-card-email-${fieldId}`}
              />
            </label>
          </div>
          {/* The person-or-company label. It has exactly one job: gating the split above. Nothing
              else reads it, and nothing infers it — the planner says which. */}
          <div className="mt-2 flex items-center gap-3" data-testid={`contact-card-kind-${fieldId}`}>
            {(["person", "organisation"] as const).map((k) => (
              <label key={k} className="flex items-center gap-1 text-[12px] text-muted-foreground">
                <input
                  type="radio"
                  name={`contact-kind-${fieldId}`}
                  checked={draft.kind === k}
                  onChange={() => setDraft((d) => ({ ...d, kind: k }))}
                  data-testid={`contact-card-kind-${k}-${fieldId}`}
                />
                {k === "person" ? "A person" : "A company or organisation"}
              </label>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => saveCard.mutate()}
                disabled={saveCard.isPending}
                className="rounded bg-[#4F6B51] px-3 py-1 text-[13px] font-medium text-white disabled:opacity-50"
                data-testid={`contact-card-done-${fieldId}`}
              >
                {saveCard.isPending ? "Saving…" : "Done"}
              </button>
              {/* Discards this card's edits. Never deletes the record — see cancelCard. */}
              <button
                type="button"
                onClick={cancelCard}
                disabled={saveCard.isPending}
                className="px-2 py-1 text-[13px] text-muted-foreground hover:text-foreground disabled:opacity-50"
                data-testid={`contact-card-cancel-${fieldId}`}
              >
                Cancel
              </button>
            </div>
            <button
              type="button"
              onClick={() => removeContact.mutate()}
              disabled={removeContact.isPending}
              className="text-[12px] text-muted-foreground hover:text-destructive disabled:opacity-50"
              data-testid={`contact-card-remove-${fieldId}`}
            >
              Remove this person
            </button>
          </div>
          {(saveCard.isError || removeContact.isError) && (
            <p className="mt-2 text-[12px] text-destructive" data-testid={`contact-card-error-${fieldId}`}>
              That didn't save. Please try again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
